import { useCallback, useMemo } from "react";
import { useProfileDetails } from "hooks/profile/profile";

/**
 * Role permissions for the signed-in user.
 *
 * They come from `profileDetails` as `resDto`, a list of
 * `{ permissionName, actionAddReqDto: { isAdd, isEdit, isDelete, isView } }`.
 * Screens used to read that list by POSITION (`hide[6]`, `resDto[1]`), which
 * silently checks the wrong page if the API ever reorders it. Always look a
 * page up by name through `can()`.
 *
 * This reuses the `profileDetails` query rather than fetching again; the query
 * is refetched by `components/requirePermission` on every route change, and
 * cleared on login and logout so one user's permissions never outlive them.
 */

/** API `permissionName` values. Several are legacy names for renamed screens. */
export const PAGES = {
    DASHBOARD: "Dashboard",
    REPORT: "Report",
    ROLES: "Roles",
    USER: "User",
    PROFILE: "Profile",
    EMPLOYEE: "Employee",
    CONTRACTOR: "Department",
    RFID_READER: "Gateway",
    SITE: "Zone",
    RFID_CARD: "Beacon",
    ALERT: "Alert",
    SHIFT: "Shift",
};

export const ACTIONS = {
    VIEW: "VIEW",
    ADD: "ADD",
    EDIT: "EDIT",
    DELETE: "DELETE",
};

const ACTION_FIELDS = {
    VIEW: "isView",
    ADD: "isAdd",
    EDIT: "isEdit",
    DELETE: "isDelete",
};

export const PROFILE_QUERY_KEY = "profileDetails";

/**
 * Where to send a user who cannot see the page they asked for: the first of
 * these they can view.
 */
export const LANDING_PAGES = [
    { page: PAGES.DASHBOARD, to: "/dashboard" },
    { page: PAGES.REPORT, to: "/reports/manpower-trend" },
    { page: PAGES.EMPLOYEE, to: "/configuration/employee" },
    { page: PAGES.CONTRACTOR, to: "/configuration/department" },
    { page: PAGES.SHIFT, to: "/configuration/shift" },
    { page: PAGES.RFID_READER, to: "/configuration/rfid" },
    { page: PAGES.SITE, to: "/configuration/zone" },
    { page: PAGES.RFID_CARD, to: "/configuration/tag" },
    { page: PAGES.USER, to: "/admin" },
    { page: PAGES.ROLES, to: "/admin/roles" },
    { page: PAGES.PROFILE, to: "/profile" },
];

const normalise = (name) => String(name ?? "").trim().toLowerCase();

/**
 * `loaded` is false until the permission list has arrived. `can()` denies
 * everything until then, so gated buttons appear once instead of flashing in
 * and out.
 */
export const usePermissions = () => {
    const { data: profileData, isError, refetch } = useProfileDetails();
    const resDto = profileData?.data?.resDto;
    const loaded = Array.isArray(resDto);

    const byName = useMemo(() => {
        const map = {};
        (Array.isArray(resDto) ? resDto : []).forEach((item) => {
            map[normalise(item?.permissionName)] = item?.actionAddReqDto || {};
        });
        return map;
    }, [resDto]);

    const can = useCallback(
        (page, action = ACTIONS.VIEW) => {
            const field = ACTION_FIELDS[action];
            return !!field && byName[normalise(page)]?.[field] === true;
        },
        [byName]
    );

    const firstPermittedPath = useMemo(
        () => LANDING_PAGES.find((item) => can(item.page, ACTIONS.VIEW))?.to || null,
        [can]
    );

    return { loaded, isError, refetch, can, firstPermittedPath };
};
